import React, { useEffect, useState } from "react";
import { db, functions } from "../../firebase";
import { collection, getDocs, query, orderBy } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";

export default function CopyNeedsTool() {
  const [needs, setNeeds] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [selectedNeedIds, setSelectedNeedIds] = useState([]);
  const [targetOrgId, setTargetOrgId] = useState("");
  const [loading, setLoading] = useState(true);
  const [copying, setCopying] = useState(false);

  useEffect(() => {
    async function loadData() {
      try {
        const [needsSnap, orgsSnap] = await Promise.all([
          getDocs(collection(db, "needs")),
          getDocs(query(collection(db, "organizations"), orderBy("orgName")))
        ]);

        setNeeds(needsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setOrgs(orgsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error("Error loading copy tool data:", err);
        alert("Error loading needs/orgs.");
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, []);

  function toggleNeed(needId) {
    setSelectedNeedIds((prev) =>
      prev.includes(needId)
        ? prev.filter((id) => id !== needId)
        : [...prev, needId]
    );
  }

  async function handleCopy() {
    if (selectedNeedIds.length === 0) {
      alert("Select at least one need.");
      return;
    }

    if (!targetOrgId) {
      alert("Select an organization to copy to.");
      return;
    }

    const targetOrg = orgs.find((o) => o.id === targetOrgId);
    const confirmed = window.confirm(
      `Copy ${selectedNeedIds.length} need(s) to ${targetOrg?.orgName || "selected org"}?`
    );

    if (!confirmed) return;

    try {
      setCopying(true);

      const copySelectedNeedsToOrg = httpsCallable(functions, "copySelectedNeedsToOrg");

      const result = await copySelectedNeedsToOrg({
        needIds: selectedNeedIds,
        targetOrgId
      });

      alert(`Copied ${result.data.copied} need(s).`);

      setSelectedNeedIds([]);
      setTargetOrgId("");
    } catch (err) {
      console.error("Error copying needs:", err);
      alert(err.message || "Error copying needs.");
    } finally {
      setCopying(false);
    }
  }

  if (loading) return <div>Loading copy tool...</div>;

  return (
    <div style={{ background: "white", border: "1px solid #eee", borderRadius: 12, padding: 20 }}>
      <h2>Copy needs to another organization</h2>

      <div style={{ marginBottom: 16 }}>
        <label style={{ display: "block", fontWeight: 700, marginBottom: 6 }}>
          Copy selected needs to:
        </label>

        <select
          value={targetOrgId}
          onChange={(e) => setTargetOrgId(e.target.value)}
          style={{ width: "100%", padding: 10 }}
        >
          <option value="">Select organization...</option>
          {orgs.map((org) => (
            <option key={org.id} value={org.id}>
              {org.orgName || org.organizationName || org.email || org.id}
            </option>
          ))}
        </select>
      </div>

      <div style={{ marginBottom: 16 }}>
        <strong>{selectedNeedIds.length}</strong> need(s) selected
      </div>

      <div style={{ maxHeight: 420, overflowY: "auto", border: "1px solid #eee", borderRadius: 8 }}>
        {needs.map((need) => (
          <label
            key={need.id}
            style={{
              display: "flex",
              gap: 10,
              padding: 12,
              borderBottom: "1px solid #f0f0f0",
              cursor: "pointer"
            }}
          >
            <input
              type="checkbox"
              checked={selectedNeedIds.includes(need.id)}
              onChange={() => toggleNeed(need.id)}
            />

            <div>
              <div style={{ fontWeight: 700 }}>
                {need.itemName || "Unnamed need"}
              </div>
              <div style={{ fontSize: 13, color: "#666" }}>
                {need.orgName || "Unknown org"} · {need.status || "no status"} · $
                {need.pricePerCycle || "TBD"} / {need.frequency || "cycle"}
              </div>
              <div style={{ fontSize: 12, color: "#999" }}>
                {need.id}
              </div>
            </div>
          </label>
        ))}
      </div>

      <button
        onClick={handleCopy}
        disabled={copying || selectedNeedIds.length === 0 || !targetOrgId}
        style={{
          marginTop: 16,
          background: "#1e3a5f",
          color: "white",
          border: "none",
          padding: "0.75rem 1.25rem",
          borderRadius: 8,
          cursor: copying ? "not-allowed" : "pointer",
          fontWeight: 700
        }}
      >
        {copying ? "Copying..." : "Copy selected needs"}
      </button>
    </div>
  );
}